import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

export interface AdminUser {
  id: string;
  auth_id: string;
  email: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  plan: string;
  concept_count: number;
  last_active_at: string | null;
  mrr_cents: number;
  created_at: string;
  // Chunk 1 additions:
  tags: string[];
  plan_notes: string | null;
  deactivated_at: string | null;
  deleted_at: string | null;
}

/**
 * List or search admin users.
 *
 * Single useQuery regardless of whether `q` is empty, to satisfy the hooks
 * rule (never early-return before all hooks). An empty / whitespace-only
 * `q` selects list mode; a non-empty `q` selects search mode.
 *
 * `includeDeleted` adds tombstoned users to the list-mode response. Search
 * always excludes tombstones (no UX driver yet — search is for active triage).
 */
export function useAdminUsers(
  filters: { q?: string; includeDeleted?: boolean } = {},
) {
  const q = (filters.q ?? "").trim();
  const includeDeleted = !!filters.includeDeleted;
  const mode = q ? "search" : "list";
  return useQuery<AdminUser[]>({
    queryKey:
      mode === "search"
        ? ["admin", "users", "search", q]
        : ["admin", "users", "list", { includeDeleted }],
    queryFn: () =>
      mode === "search"
        ? apiClient
            .get(`api/admin/users/search?q=${encodeURIComponent(q)}`)
            .json()
        : apiClient
            .get(
              `api/admin/users${includeDeleted ? "?include_deleted=true" : ""}`,
            )
            .json(),
    retry: false,
    staleTime: 30_000,
  });
}

export interface AdminUserDetail extends AdminUser {
  usage_cost_cents_total: number;
  stripe_subscription_status: "active" | "canceled" | "none";
}

/**
 * Fetch a single admin user's detail, optionally refreshing Stripe data.
 * When `refreshStripe` is true, hits `?refresh_stripe=true` (live Stripe fetch).
 */
export function useUserDetail(userId: string | null, refreshStripe = false) {
  return useQuery<AdminUserDetail>({
    queryKey: ["admin", "user-detail", userId, refreshStripe],
    queryFn: () => {
      const url = `api/admin/users/${userId}${refreshStripe ? "?refresh_stripe=true" : ""}`;
      return apiClient.get(url).json();
    },
    enabled: !!userId,
    staleTime: 10_000,
    retry: false,
  });
}

export interface AdminAction {
  id: number;
  admin_user_id: string;
  action: string;
  target_user_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

/**
 * Fetch a page of admin audit rows for the given user. Sort is id DESC.
 * Pass `beforeId` to fetch the next (older) page.
 */
export function useUserAudit(
  userId: string | null,
  beforeId?: number,
  limit = 50,
) {
  return useQuery<AdminAction[]>({
    queryKey: ["admin", "user-audit", userId, beforeId ?? null, limit],
    queryFn: () => {
      const params = new URLSearchParams();
      if (beforeId !== undefined) params.set("before_id", String(beforeId));
      params.set("limit", String(limit));
      return apiClient
        .get(`api/admin/users/${userId}/audit?${params}`)
        .json();
    },
    enabled: !!userId,
    staleTime: 5_000,
    retry: false,
  });
}

export interface AdminUserConcept {
  id: string;
  name: string;
  created_at: string;
  paper_count: number;
  monitor_enabled: boolean;
  last_monitor_run: string | null;
  next_monitor_run: string | null;
  last_monitor_duration_seconds: number | null;
  last_monitor_status:
    | "success"
    | "failure"
    | "skipped_no_foundational"
    | "cancelled"
    | null;
  last_monitor_new_papers: number | null;
  monitor_consecutive_failures: number;
  last_monitor_error: string | null;
  last_monitor_error_at: string | null;
}

/**
 * Fetch the user's concepts for the UserDetailDrawer Concepts tab.
 * Newest-first, includes monitor status and paper count.
 */
export function useUserConcepts(userId: string | null) {
  return useQuery<AdminUserConcept[]>({
    queryKey: ["admin", "user-concepts", userId],
    queryFn: () => apiClient.get(`api/admin/users/${userId}/concepts`).json(),
    enabled: !!userId,
    staleTime: 10_000,
    retry: false,
  });
}

export function useSetUserPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, plan }: { userId: string; plan: string }) =>
      apiClient.patch(`api/admin/users/${userId}/plan`, { json: { plan } }),
    onMutate: async ({ userId, plan }) => {
      await qc.cancelQueries({ queryKey: ["admin", "users"] });
      const lists = qc.getQueriesData<AdminUser[]>({
        queryKey: ["admin", "users"],
      });
      for (const [key, prev] of lists) {
        if (!prev) continue;
        qc.setQueryData<AdminUser[]>(
          key,
          prev.map((u) => (u.id === userId ? { ...u, plan } : u)),
        );
      }
      return { lists };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.lists) {
        for (const [key, prev] of ctx.lists) qc.setQueryData(key, prev);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiClient.post(`api/admin/users/${userId}/deactivate`),
    onMutate: async (userId: string) => {
      await qc.cancelQueries({ queryKey: ["admin", "users"] });
      const lists = qc.getQueriesData<AdminUser[]>({
        queryKey: ["admin", "users"],
      });
      const now = new Date().toISOString();
      for (const [key, prev] of lists) {
        if (!prev) continue;
        qc.setQueryData<AdminUser[]>(
          key,
          prev.map((u) =>
            u.id === userId ? { ...u, deactivated_at: now } : u,
          ),
        );
      }
      return { lists };
    },
    onError: (_err, _userId, ctx) => {
      if (ctx?.lists) {
        for (const [key, prev] of ctx.lists) qc.setQueryData(key, prev);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export interface DeletePreview {
  // Rows that WILL be deleted.
  concept_count: number;
  paper_highlight_count: number;
  paper_pdf_count: number;
  per_user_ai_summary_count: number;
  r2_blobs_to_delete: number;
  r2_blobs_total_bytes: number;
  r2_blobs_unknown_size_count: number;
  // Rows RETAINED for analytics (NOT deleted; user_id preserved on the
  // tombstoned users row keeps FK integrity). Modal renders these in a
  // separate section so the admin isn't misled about deletion impact.
  retained_usage_event_count: number;
  retained_pdf_retrieval_event_count: number;
  retained_feedback_event_count: number;
  stripe_subscription_status: "active" | "canceled" | "none";
  clerk_user_exists: boolean;
  warnings: string[];
}

/**
 * Fetch the delete preview for a user. Disabled until userId is non-null.
 * Kept `staleTime: 0` so re-opening the modal always re-fetches fresh counts.
 */
export function useDeletePreview(userId: string | null) {
  return useQuery<DeletePreview>({
    queryKey: ["admin", "delete-preview", userId],
    queryFn: () =>
      apiClient.get(`api/admin/users/${userId}/delete-preview`).json(),
    enabled: !!userId,
    staleTime: 0,
    retry: false,
  });
}

export interface R2DeleteError {
  key: string;
  error: string;
}

export interface HardDeleteResponse {
  tombstoned: boolean;
  clerk_ok: boolean | null;
  stripe_ok: boolean | null;
  r2_blobs_deleted: number;
  r2_errors: R2DeleteError[];
}

/**
 * Hard-delete a user. Pessimistic (destructive) — no optimistic cache write.
 * On settle, invalidates the users list so the row disappears / shows tombstone.
 */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<
    HardDeleteResponse,
    Error,
    { userId: string; confirmEmail: string }
  >({
    mutationFn: ({ userId, confirmEmail }) =>
      apiClient
        .delete(`api/admin/users/${userId}`, {
          json: { confirm_email: confirmEmail },
          timeout: 120_000,
        })
        .json(),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useSetUserTags() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { userId: string; tags: string[] },
    { lists: [readonly unknown[], AdminUser[] | undefined][] }
  >({
    mutationFn: ({ userId, tags }) =>
      apiClient
        .patch(`api/admin/users/${userId}/tags`, { json: { tags } })
        .then(() => undefined),
    onMutate: async ({ userId, tags }) => {
      await qc.cancelQueries({ queryKey: ["admin"] });
      const lists = qc.getQueriesData<AdminUser[]>({
        queryKey: ["admin", "users"],
      });
      for (const [key, prev] of lists) {
        if (!prev) continue;
        qc.setQueryData<AdminUser[]>(
          key,
          prev.map((u) => (u.id === userId ? { ...u, tags } : u)),
        );
      }
      return { lists };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.lists) {
        for (const [key, prev] of ctx.lists) qc.setQueryData(key, prev);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}

export function useSetUserPlanNotes() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { userId: string; planNotes: string | null },
    { lists: [readonly unknown[], AdminUser[] | undefined][] }
  >({
    mutationFn: ({ userId, planNotes }) =>
      apiClient
        .patch(`api/admin/users/${userId}/plan-notes`, {
          json: { plan_notes: planNotes },
        })
        .then(() => undefined),
    onMutate: async ({ userId, planNotes }) => {
      await qc.cancelQueries({ queryKey: ["admin"] });
      const lists = qc.getQueriesData<AdminUser[]>({
        queryKey: ["admin", "users"],
      });
      for (const [key, prev] of lists) {
        if (!prev) continue;
        qc.setQueryData<AdminUser[]>(
          key,
          prev.map((u) =>
            u.id === userId ? { ...u, plan_notes: planNotes } : u,
          ),
        );
      }
      return { lists };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.lists) {
        for (const [key, prev] of ctx.lists) qc.setQueryData(key, prev);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}

export function useRestoreUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiClient.post(`api/admin/users/${userId}/restore`),
    onMutate: async (userId: string) => {
      await qc.cancelQueries({ queryKey: ["admin", "users"] });
      const lists = qc.getQueriesData<AdminUser[]>({
        queryKey: ["admin", "users"],
      });
      for (const [key, prev] of lists) {
        if (!prev) continue;
        qc.setQueryData<AdminUser[]>(
          key,
          prev.map((u) =>
            u.id === userId ? { ...u, deactivated_at: null } : u,
          ),
        );
      }
      return { lists };
    },
    onError: (_err, _userId, ctx) => {
      if (ctx?.lists) {
        for (const [key, prev] of ctx.lists) qc.setQueryData(key, prev);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}
